import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Tooltip, Typography } from '@blade/ui-kit'
import { MakerIcon } from '@root/entities/trade/ui'
import { walletAddressesAtom } from '@root/entities/wallet/atoms'
import { MakerPopup } from '@root/features/services/maker-stats/ui'
import { walletProfileModalAtom } from '@root/features/services/wallet-profile/atoms'
import { useTrackedWallet } from '@root/features/wallet-tracker/hooks'
import { useTranslation } from '@root/shared/localization'
import {
  BackingIcon,
  CrownOutlinedIcon,
  ReservedSpace,
  Truncate,
} from '@root/shared/ui-kit'

import { TradeMakerCellOwner } from './trade-maker-cell-owner'
import { TradeMakerCellTags } from './trade-maker-cell-tags'
import { TradeMakerCellTracked } from './trade-maker-cell-tracked'
import { TableMakerCellTxs } from './trade-maker-cell-txs'

import type { Network } from '@root/entities/network'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { Maker } from '@root/entities/trade'
import type { ClassNameProps } from '@root/shared/react'

export type TableMakerCellProps = {
  childrenPosition?: 'start' | 'end'
  makerIconPosition?: 'start' | 'end'
  children?: React.ReactNode
  network: Network.Network
  maker: Maker.MakerShort
  color?: string
  pairAddress: PairAddress
  pair: PairModel | undefined
  withTags?: boolean
  /**
   * Change user wallet address to 'You'
   */
  aliasUserWallet?: boolean
  /**
   * Whether a wallet that belongs to somebody here shows their name.
   *
   * On by default: the server only sends an owner for an account that has a
   * name and has not gone incognito, so there is nothing to decide per table
   * — either there is a person behind the address or there is not.
   */
  showOwner?: boolean
} & ClassNameProps

export const TableMakerCell: React.FC<TableMakerCellProps> = ({
  maker,
  network,
  childrenPosition,
  children,
  color,
  className,
  pairAddress,
  pair,
  makerIconPosition = 'start',
  withTags = true,
  aliasUserWallet = false,
  showOwner = true,
}) => {
  const { t } = useTranslation()

  const [developerAddress] = useAtom(
    (ctx) => {
      if (!pair) {
        return undefined
      }
      return ctx.spy(pair.initTxCreator)?.address
    },
    [pair],
  )
  const [isUserTrade] = useAtom((ctx) => {
    const userWalletAddresses = ctx.spy(walletAddressesAtom)
    return userWalletAddresses.has(maker.address)
  })
  const makerAddressSlice = useMemo(
    () => maker.address.slice(-8),
    [maker.address],
  )
  const trackedWallet = useTrackedWallet(maker.address)

  const openProfile = useAction(
    (ctx) =>
      walletProfileModalAtom(ctx, { walletAddress: maker.address, network }),
    [maker.address, network],
  )

  /**
   * Opens the wallet's profile rather than the block explorer.
   *
   * The explorer is a click away inside the profile. It used to be what this
   * did, and it was the only thing an address could do — which meant the
   * figures we hold about a wallet were reachable only by hovering and only
   * for the pair on screen.
   */
  const handleAddressClick = () => {
    openProfile()
  }

  const renderDeveloperIcon = () => {
    if (developerAddress !== maker.address) {
      return undefined
    }
    return (
      <Tooltip content={t('trading.dev.short')}>
        <BackingIcon size={14} />
      </Tooltip>
    )
  }

  const renderWalletAddress = () => {
    if (trackedWallet && trackedWallet.label !== maker.address) {
      return (
        <Address_ type="button" onClick={handleAddressClick}>
          <TradeMakerCellTracked wallet={trackedWallet} />
        </Address_>
      )
    }

    if (isUserTrade && aliasUserWallet) {
      return (
        <YouLabel>
          <span>{t('common.you')}</span>
          <CrownOutlinedIcon size={14} />
        </YouLabel>
      )
    }

    if (showOwner && maker.owner) {
      return <TradeMakerCellOwner owner={maker.owner} />
    }

    return (
      <Address_ type="button" onClick={handleAddressClick}>
        <ReservedSpace $chars={8}>
          <Truncate start={4} end={4}>
            {makerAddressSlice}
          </Truncate>
        </ReservedSpace>
      </Address_>
    )
  }

  return (
    <Root $color={color} className={className}>
      {childrenPosition === 'start' ? children : undefined}
      {makerIconPosition === 'start' ? (
        <>
          <MakerIcon volume={maker.volumeInUsd} />
        </>
      ) : (
        <>
          {withTags ? <TradeMakerCellTags maker={maker} /> : undefined}
          {renderDeveloperIcon()}
        </>
      )}
      <MakerPopup
        maker={maker}
        network={network}
        pairAddress={pairAddress}
        pair={pair}
      >
        <PopupContainer>
          <AddressLabel>{renderWalletAddress()}</AddressLabel>
          <TableMakerCellTxs maker={maker} />
        </PopupContainer>
      </MakerPopup>

      {makerIconPosition === 'end' ? (
        <>
          <MakerIcon volume={maker.volumeInUsd} />
        </>
      ) : (
        <>
          {renderDeveloperIcon()}
          {withTags ? <TradeMakerCellTags maker={maker} /> : undefined}
        </>
      )}
      {childrenPosition === 'end' ? children : undefined}
    </Root>
  )
}

const Root = styled.div<{ $color?: string }>`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  align-items: center;
  justify-content: start;
  gap: 6px;

  color: ${(props) => props.$color ?? 'white'};

  // TODO: Wait for designers for monospace font
  font-family:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    Liberation Mono,
    Courier New,
    monospace;
`

const Address_ = styled.button`
  all: unset;
  cursor: pointer;
`
const AddressLabel = styled(Typography)`
  white-space: nowrap;
`

const PopupContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 8px;
`

const YouLabel = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  font-weight: 600;
  font-family: ${({ theme }) => theme.fontFamily};

  color: var(--amber-9);
`
